import { useState, useMemo, useEffect } from 'react';
import { formatStatus, getCloudName, getSubmittedBy, formatTimestamp } from '../utils/requestHelpers';

/**
 * Custom hook for managing request filters with advanced search capabilities
 * Handles filter state and filtering logic for requests
 * @param {array} requests - Array of requests to filter
 * @param {string} storageKey - Optional localStorage key for saving filters
 * @returns {object} Filter state and filtered results
 */
export const useRequestFilters = (requests = [], storageKey = null) => {
    // Basic filters
    const [statusFilter, setStatusFilter] = useState('');
    const [cloudNameFilter, setCloudNameFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [requestIdFilter, setRequestIdFilter] = useState('');
    
    // Advanced filters
    const [globalSearch, setGlobalSearch] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]); // Multi-select
    const [selectedCategories, setSelectedCategories] = useState([]); // Multi-select
    const [dateRangeStart, setDateRangeStart] = useState('');
    const [dateRangeEnd, setDateRangeEnd] = useState('');
    const [submittedByFilter, setSubmittedByFilter] = useState('');

    // Saved filters
    const [savedFilters, setSavedFilters] = useState([]);
    const [filterPresetName, setFilterPresetName] = useState('');

    // Load saved filters from localStorage on mount
    useEffect(() => {
        if (storageKey) {
            const saved = localStorage.getItem(`tb_filters_${storageKey}`);
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    setSavedFilters(parsed);
                } catch (e) {
                    console.error('Error loading saved filters:', e);
                }
            }
        }
    }, [storageKey]);

    const hasActiveFilters = useMemo(() => {
        return !!(
            statusFilter ||
            cloudNameFilter ||
            categoryFilter ||
            requestIdFilter ||
            globalSearch ||
            selectedStatuses.length > 0 ||
            selectedCategories.length > 0 ||
            dateRangeStart ||
            dateRangeEnd ||
            submittedByFilter
        );
    }, [statusFilter, cloudNameFilter, categoryFilter, requestIdFilter, globalSearch, selectedStatuses, selectedCategories, dateRangeStart, dateRangeEnd, submittedByFilter]);

    // Get unique values for multi-select options
    const availableStatuses = useMemo(() => {
        const statuses = new Set();
        requests.forEach(request => {
            if (request.status) {
                statuses.add(request.status);
            }
        });
        return Array.from(statuses).sort();
    }, [requests]);

    const availableCategories = useMemo(() => {
        const categories = new Set();
        requests.forEach(request => {
            if (request.category) {
                categories.add(request.category);
            }
        });
        return Array.from(categories).sort();
    }, [requests]);

    // Apply filters to requests
    const filteredRequests = useMemo(() => {
        if (!requests || requests.length === 0) return [];

        return requests.filter(request => {
            // Basic filters (backward compatible)
            if (statusFilter && !formatStatus(request.status || '').toLowerCase().includes(statusFilter.toLowerCase())) {
                return false;
            }
            if (cloudNameFilter && !getCloudName(request).toLowerCase().includes(cloudNameFilter.toLowerCase())) {
                return false;
            }
            if (categoryFilter && !(request.category || '').toLowerCase().includes(categoryFilter.toLowerCase())) {
                return false;
            }
            if (requestIdFilter && !(request.id || '').toLowerCase().includes(requestIdFilter.toLowerCase())) {
                return false;
            }

            // Multi-select filters
            if (selectedStatuses.length > 0 && !selectedStatuses.includes(request.status)) {
                return false;
            }
            if (selectedCategories.length > 0 && !selectedCategories.includes(request.category)) {
                return false;
            }

            // Date range filter
            if (dateRangeStart || dateRangeEnd) {
                const requestDate = new Date(request.timestamp);
                if (dateRangeStart) {
                    const startDate = new Date(dateRangeStart);
                    startDate.setHours(0, 0, 0, 0);
                    if (requestDate < startDate) return false;
                }
                if (dateRangeEnd) {
                    const endDate = new Date(dateRangeEnd);
                    endDate.setHours(23, 59, 59, 999);
                    if (requestDate > endDate) return false;
                }
            }

            // Submitted by filter
            if (submittedByFilter) {
                const submittedBy = getSubmittedBy(request).toLowerCase();
                if (!submittedBy.includes(submittedByFilter.toLowerCase())) {
                    return false;
                }
            }

            // Global search - searches across all fields
            if (globalSearch) {
                const searchLower = globalSearch.toLowerCase();
                const searchableText = [
                    request.id || '',
                    getCloudName(request),
                    request.category || '',
                    formatStatus(request.status || ''),
                    getSubmittedBy(request),
                    request.approver || '',
                    request.subApprover || '',
                    request.approverComment || '',
                    request.subApproverComment || '',
                    formatTimestamp(request.timestamp)
                ].join(' ').toLowerCase();

                if (!searchableText.includes(searchLower)) {
                    return false;
                }
            }

            return true;
        });
    }, [
        requests,
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        globalSearch,
        selectedStatuses,
        selectedCategories,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter
    ]);

    const resetFilters = () => {
        setStatusFilter('');
        setCloudNameFilter('');
        setCategoryFilter('');
        setRequestIdFilter('');
        setGlobalSearch('');
        setSelectedStatuses([]);
        setSelectedCategories([]);
        setDateRangeStart('');
        setDateRangeEnd('');
        setSubmittedByFilter('');
    };

    const saveCurrentFilters = () => {
        if (!filterPresetName.trim() || !storageKey) return;

        const filterPreset = {
            id: Date.now().toString(),
            name: filterPresetName.trim(),
            filters: {
                statusFilter,
                cloudNameFilter,
                categoryFilter,
                requestIdFilter,
                globalSearch,
                selectedStatuses,
                selectedCategories,
                dateRangeStart,
                dateRangeEnd,
                submittedByFilter
            },
            createdAt: new Date().toISOString()
        };

        const updated = [...savedFilters, filterPreset];
        setSavedFilters(updated);
        localStorage.setItem(`tb_filters_${storageKey}`, JSON.stringify(updated));
        setFilterPresetName('');
    };

    const loadFilterPreset = (preset) => {
        if (!preset || !preset.filters) return;
        
        setStatusFilter(preset.filters.statusFilter || '');
        setCloudNameFilter(preset.filters.cloudNameFilter || '');
        setCategoryFilter(preset.filters.categoryFilter || '');
        setRequestIdFilter(preset.filters.requestIdFilter || '');
        setGlobalSearch(preset.filters.globalSearch || '');
        setSelectedStatuses(preset.filters.selectedStatuses || []);
        setSelectedCategories(preset.filters.selectedCategories || []);
        setDateRangeStart(preset.filters.dateRangeStart || '');
        setDateRangeEnd(preset.filters.dateRangeEnd || '');
        setSubmittedByFilter(preset.filters.submittedByFilter || '');
    };

    const deleteFilterPreset = (presetId) => {
        const updated = savedFilters.filter(p => p.id !== presetId);
        setSavedFilters(updated);
        if (storageKey) {
            localStorage.setItem(`tb_filters_${storageKey}`, JSON.stringify(updated));
        }
    };

    return {
        // Basic filter values
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        // Advanced filter values
        globalSearch,
        selectedStatuses,
        selectedCategories,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter,
        // Saved filters
        savedFilters,
        filterPresetName,
        // Available options
        availableStatuses,
        availableCategories,
        // Basic setters
        setStatusFilter,
        setCloudNameFilter,
        setCategoryFilter,
        setRequestIdFilter,
        // Advanced setters
        setGlobalSearch,
        setSelectedStatuses,
        setSelectedCategories,
        setDateRangeStart,
        setDateRangeEnd,
        setSubmittedByFilter,
        setFilterPresetName,
        // Computed values
        hasActiveFilters,
        filteredRequests,
        // Actions
        resetFilters,
        saveCurrentFilters,
        loadFilterPreset,
        deleteFilterPreset
    };
};

